import { History, LogOut, Rocket, User, Diamond, Receipt, Menu, MessageSquare } from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";

interface Profile {
  email?: string;
  plan?: string;
  plan_expires_at?: string | null;
}

const AppSidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile>({});

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('email, plan, plan_expires_at')
          .eq('id', session.user.id)
          .single();
        
        if (profileData) {
          setProfile(profileData);
        }
      }
    };

    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate("/");
      toast.success("Đăng xuất thành công");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Lỗi đăng xuất. Vui lòng thử lại sau.");
    }
  };

  const getInitials = (email: string) => {
    return email?.charAt(0).toUpperCase() || 'U';
  };

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getPlanDisplay = () => {
    if (profile.plan === 'basic') {
      return (
        <div className="flex items-center gap-1.5">
          <Diamond className="h-4 w-4" style={{ color: '#9b87f5' }} />
          <div className="flex flex-col">
            <span style={{ color: '#9b87f5' }} className="font-medium">Gói Pro</span>
            {profile.plan_expires_at && (
              <span className="text-xs text-muted-foreground">
                Hết hạn: {formatExpirationDate(profile.plan_expires_at)}
              </span>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        <User className="h-4 w-4 text-muted-foreground" />
        <span>Gói Miễn phí</span>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="space-y-2 px-2 py-2 sm:px-4">
        <div className="flex items-center gap-3 rounded-lg border p-2 sm:p-3">
          <Avatar>
            <AvatarFallback>{getInitials(profile.email || '')}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col overflow-hidden flex-1 gap-y-1">
            <span className="truncate text-sm">{profile.email}</span>
            <span className="text-xs text-muted-foreground">
              {getPlanDisplay()}
            </span>
          </div>
        </div>

        <Button 
          asChild 
          variant="outline" 
          className="w-full justify-start gap-2 text-sm"
          onClick={onItemClick}
        >
          <Link to="/plans">
            <Rocket className="h-4 w-4" />
            <span>{profile.plan === 'basic' ? 'Quản lý gói' : 'Nâng cấp'}</span>
          </Link>
        </Button>

        <SidebarMenuButton asChild onClick={onItemClick}>
          <Link 
            to="/chat"
            className={`flex w-full items-center gap-2 rounded-md p-2 text-sm ${location.pathname === "/chat" ? "bg-accent" : ""}`}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Chat</span>
          </Link>
        </SidebarMenuButton>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild onClick={onItemClick}>
                  <Link 
                    to="/transactions"
                    className={`text-sm ${location.pathname === "/transactions" ? "bg-accent" : ""}`}
                  >
                    <Receipt className="w-4 h-4" />
                    <span>Lịch sử giao dịch</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild onClick={onItemClick}>
                  <Link 
                    to="/contact"
                    className={`text-sm ${location.pathname === "/contact" ? "bg-accent" : ""}`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Liên hệ và Phản hồi</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </div>

      <div className="mt-auto mb-4">
        <Button
          variant="destructive"
          className="w-full flex items-center gap-2 justify-center px-4 py-2 text-sm font-medium"
          onClick={() => {
            handleSignOut();
            onItemClick?.();
          }}
        >
          <LogOut className="h-4 w-4" />
          <span>Đăng xuất</span>
        </Button>
      </div>
    </div>
  );
};

export function AppSidebar() {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 1324px)");
  const [isOpen, setIsOpen] = useState(false);
  const showSidebar = location.pathname !== "/" || !!localStorage.getItem("sb-moslxznaqtmjyvpzslsg-auth-token");

  if (!showSidebar) return null;

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className="fixed top-4 right-4 z-[60]"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[270px] p-0">
          <AppSidebarContent onItemClick={() => setIsOpen(false)} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sidebar className="hidden lg:block fixed left-0 top-0 bottom-0 w-[270px] z-50">
      <AppSidebarContent />
    </Sidebar>
  );
}
